/**
 * Panel lateral con los apuntes de una cuenta.
 *
 * Los movimientos NO se intercalan en el estado financiero. Cien filas dentro
 * de un ESF lo vuelven ilegible, y el contador que abre un renglón quiere ver
 * su composición, no perder el hilo del documento.
 *
 * Cada apunte muestra su CONTRIBUCIÓN al reporte además del saldo de Odoo: son
 * cifras distintas cuando una regla de presentación toca la cuenta, y
 * enseñar solo una de las dos deja al lector comparando peras con manzanas.
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/client";
import { useAsync } from "../api/useAsync";
import type { EntryPage, TreeNode } from "../api/types";
import type { Period } from "./period";
import { ErrorState, Loading } from "./States";
import { formatDate, formatMoney } from "./format";

const PAGINA = 100;

interface Props {
  reportKey: string;
  node: TreeNode | null;
  period: Period;
  onClose: () => void;
}

export function EntriesDrawer({ reportKey, node, period, onClose }: Props) {
  const [offset, setOffset] = useState(0);
  const [nodoPrevio, setNodoPrevio] = useState<string | null>(null);

  // Al cambiar de cuenta se vuelve a la primera página. Se ajusta DURANTE el
  // render, no en un efecto: un efecto provocaría un render de más mostrando
  // la página anterior con los datos de la cuenta nueva.
  if (node && node.node_id !== nodoPrevio) {
    setNodoPrevio(node.node_id);
    setOffset(0);
  }

  const { data, error, loading } = useAsync<EntryPage | null>(
    () =>
      node
        ? api.nodeEntries(reportKey, node.node_id, period, PAGINA, offset)
        : Promise.resolve(null),
    [reportKey, node?.node_id, period.periodFrom, period.periodTo, offset],
  );

  const page = data ?? null;

  if (!node) return null;

  return (
    <aside className="drawer" aria-label={`Movimientos de ${node.code}`}>
      <div className="drawer-head">
        <div>
          <h2>
            <span className="mono">{node.code}</span> · {node.label}
          </h2>
          <p className="small muted">
            Saldo {formatMoney(node.presented_closing_amount)} ·{" "}
            {page?.total ?? node.entry_count} movimientos
            {node.odoo_path && ` · ruta Odoo: ${node.odoo_path}`}
          </p>
        </div>
        <button type="button" className="btn" onClick={onClose}>
          Cerrar
        </button>
      </div>

      {loading && <Loading what="los movimientos" />}
      {error && <ErrorState error={error} />}

      {page && !loading && (
        <>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th scope="col">Fecha</th>
                  <th scope="col">Asiento</th>
                  <th scope="col">Concepto</th>
                  <th scope="col" className="num">
                    Saldo Odoo
                  </th>
                  <th scope="col" className="num">
                    En el reporte
                  </th>
                </tr>
              </thead>
              <tbody>
                {page.rows.map((row) => (
                  <tr key={`${row.move_line_id}-${row.origin}`}>
                    <td>{formatDate(row.accounting_date)}</td>
                    <td className="mono">{row.move_name}</td>
                    <td>
                      {row.line_description ?? ""}
                      {row.rule_id && (
                        <span className="small muted"> ↳ {row.rule_id}</span>
                      )}
                    </td>
                    <td className="num">{formatMoney(row.odoo_balance)}</td>
                    <td className="num">{formatMoney(row.presented_amount)}</td>
                  </tr>
                ))}
                {page.rows.length === 0 && (
                  <tr>
                    <td colSpan={5} className="muted">
                      Sin movimientos en el período.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="drawer-foot">
            <button
              type="button"
              className="btn"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGINA))}
            >
              Anteriores
            </button>
            <span className="small muted">
              {offset + 1}–{Math.min(offset + PAGINA, page.total)} de {page.total}
            </span>
            <button
              type="button"
              className="btn"
              disabled={offset + PAGINA >= page.total}
              onClick={() => setOffset(offset + PAGINA)}
            >
              Siguientes
            </button>
            <div className="spacer" />
            <Link className="btn" to={`/reports/general_ledger`}>
              Abrir Libro Mayor
            </Link>
          </div>
        </>
      )}
    </aside>
  );
}
